import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Camera, Video, Upload, Loader2, X, MapPin, Phone, Mail, User, FileText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

// Departamentos y Municipios de Honduras
const hondurasDepartamentos = {
  "Choluteca": [
    "Choluteca", "Apacilagua", "Concepción de María", "Duyure", "El Corpus", 
    "El Triunfo", "Marcovia", "Morolica", "Namasigüe", "Orocuina", 
    "Pespire", "San Antonio de Flores", "San Isidro", "San José", 
    "San Marcos de Colón", "Santa Ana de Yusguare"
  ],
  "Valle": [
    "Nacaome", "Alianza", "Amapala", "Aramecina", "Caridad", "Goascorán", 
    "Langue", "San Francisco de Coray", "San Lorenzo"
  ],
  "Francisco Morazán": [
    "Tegucigalpa", "Alubarén", "Cedros", "Curarén", "El Porvenir", "Guaimaca",
    "La Libertad", "La Venta", "Lepaterique", "Maraita", "Marale", "Nueva Armenia",
    "Ojojona", "Orica", "Reitoca", "Sabanagrande", "San Antonio de Oriente",
    "San Buenaventura", "San Ignacio", "San Juan de Flores", "San Miguelito",
    "Santa Ana", "Santa Lucía", "Talanga", "Tatumbla", "Valle de Ángeles", 
    "Villa de San Francisco", "Vallecillo"
  ],
  "Comayagua": [
    "Comayagua", "Ajuterique", "El Rosario", "Esquías", "Humuya", "La Libertad",
    "Lamaní", "La Trinidad", "Lejamaní", "Meámbar", "Minas de Oro", "Ojo de Agua",
    "San Jerónimo", "San José de Comayagua", "San José del Potrero", 
    "San Luis", "San Sebastián", "Siguatepeque", "Taulabé", "Villa de San Antonio"
  ],
  "Cortés": [
    "San Pedro Sula", "Choloma", "La Lima", "Puerto Cortés", "Omoa", "Pimienta",
    "Potrerillos", "San Antonio de Cortés", "San Francisco de Yojoa", 
    "San Manuel", "Santa Cruz de Yojoa", "Villanueva"
  ],
  "Atlántida": [
    "La Ceiba", "Arizona", "El Porvenir", "Esparta", "Jutiapa", "La Masica",
    "San Francisco", "Tela"
  ],
  "Yoro": [
    "Yoro", "Arenal", "El Negrito", "El Progreso", "Jocón", "Morazán",
    "Olanchito", "Santa Rita", "Sulaco", "Victoria", "Yorito"
  ],
  "Olancho": [
    "Juticalpa", "Campamento", "Catacamas", "Concordia", "Dulce Nombre de Culmí",
    "El Rosario", "Esquipulas del Norte", "Gualaco", "Guarizama", "Guata",
    "Guayape", "Jano", "La Unión", "Mangulile", "Manto", "Patuca", "Salamá",
    "San Esteban", "San Francisco de Becerra", "San Francisco de la Paz",
    "Santa María del Real", "Silca", "Yocón"
  ],
  "Santa Bárbara": [
    "Santa Bárbara", "Arada", "Atima", "Azacualpa", "Ceguaca", "Chinda",
    "Concepción del Norte", "Concepción del Sur", "El Níspero", "Gualala",
    "Ilama", "Las Vegas", "Macuelizo", "Naranjito", "Nuevo Celilac",
    "Nueva Frontera", "Petoa", "Protección", "Quimistán", "San Francisco de Ojuera",
    "San José de Colinas", "San Luis", "San Marcos", "San Nicolás", "San Pedro Zacapa",
    "San Vicente Centenario", "Santa Rita", "Trinidad"
  ],
  "Copán": [
    "Santa Rosa de Copán", "Cabañas", "Concepción", "Copán Ruinas", "Corquín",
    "Cucuyagua", "Dolores", "Dulce Nombre", "El Paraíso", "Florida",
    "La Jigua", "La Unión", "Nueva Arcadia", "San Agustín", "San Antonio",
    "San Jerónimo", "San José", "San Juan de Opoa", "San Nicolás", 
    "San Pedro", "Santa Rita", "Trinidad de Copán", "Veracruz"
  ],
  "Lempira": [
    "Gracias", "Belén", "Candelaria", "Cololaca", "Erandique", "Gualcince",
    "Guarita", "La Campa", "La Iguala", "Las Flores", "La Unión", "La Virtud",
    "Lepaera", "Mapulaca", "Piraera", "San Andrés", "San Francisco",
    "San Juan Guarita", "San Manuel Colohete", "San Rafael", "San Sebastián",
    "Santa Cruz", "Talgua", "Tambla", "Tomalá", "Valladolid", "Virginia"
  ],
  "Intibucá": [
    "La Esperanza", "Camasca", "Colomoncagua", "Concepción", "Dolores",
    "Intibucá", "Jesús de Otoro", "Magdalena", "Masaguara", "San Antonio",
    "San Isidro", "San Juan", "San Marcos de la Sierra", "San Miguelito",
    "Santa Lucía", "Yamaranguila"
  ],
  "La Paz": [
    "La Paz", "Aguanqueterique", "Cabañas", "Cane", "Chinacla", "Guajiquiro",
    "Lauterique", "Marcala", "Mercedes de Oriente", "Opatoro", "San Antonio del Norte",
    "San José", "San Juan", "San Pedro de Tutule", "Santa Ana", "Santa Elena",
    "Santa María", "Santiago de Puringla", "Yarula"
  ],
  "El Paraíso": [
    "Yuscarán", "Alauca", "Danlí", "El Paraíso", "Guinope", "Jacaleapa",
    "Liure", "Morocelí", "Oropolí", "Potrerillos", "San Antonio de Flores",
    "San Lucas", "San Matías", "Soledad", "Teupasenti", "Texiguat",
    "Trojes", "Vado Ancho"
  ],
  "Ocotepeque": [
    "Ocotepeque", "Belén Gualcho", "Concepción", "Dolores Merendón", "Fraternidad",
    "La Encarnación", "La Labor", "Lucerna", "Mercedes", "San Fernando",
    "San Francisco del Valle", "San Jorge", "San Marcos", "Santa Fe", "Sensenti", "Sinuapa"
  ],
  "Colón": [
    "Trujillo", "Balfate", "Bonito Oriental", "Iriona", "Limón", "Sabá",
    "Santa Fe", "Santa Rosa de Aguán", "Sonaguera", "Tocoa"
  ],
  "Gracias a Dios": [
    "Puerto Lempira", "Brus Laguna", "Ahuas", "Juan Francisco Bulnes", 
    "Ramón Villeda Morales", "Wampusirpi"
  ],
  "Islas de la Bahía": [
    "Roatán", "Guanaja", "José Santos Guardiola", "Utila"
  ]
};

export default function CompartirContenidoPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    nombre: "",
    correo: "",
    telefono: "",
    descripcion: "",
    departamento: "Choluteca",
    municipio: "",
  });

  const municipios = hondurasDepartamentos[formData.departamento] || [];

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > 10) {
      toast.error("Máximo 10 fotos permitidas");
      return;
    }

    setUploading(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);

        const response = await axios.post(`${API}/media/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newPhotos.push({
          media_id: response.data.media_id,
          url: response.data.url,
          filename: response.data.filename
        });
      } catch (error) {
        console.error("Error uploading photo:", error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setPhotos([...photos, ...newPhotos]);
    setUploading(false);
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (videos.length + files.length > 5) {
      toast.error("Máximo 5 videos permitidos");
      return;
    }

    setUploading(true);
    const newVideos = [];

    for (const file of files) {
      // Check file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name} es muy grande (máx 100MB)`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('title', file.name);

        const response = await axios.post(`${API}/media/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        newVideos.push({
          media_id: response.data.media_id,
          url: response.data.url,
          filename: response.data.filename
        });
      } catch (error) {
        console.error("Error uploading video:", error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setVideos([...videos, ...newVideos]);
    setUploading(false);
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.correo || !formData.telefono || !formData.descripcion) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }

    if (photos.length === 0 && videos.length === 0) {
      toast.error("Por favor agrega al menos una foto o video");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/community-content`, {
        nombre: formData.nombre,
        correo: formData.correo,
        telefono: formData.telefono,
        descripcion: formData.descripcion,
        departamento: formData.departamento,
        municipio: formData.municipio,
        photo_ids: photos.map(p => p.media_id),
        video_ids: videos.map(v => v.media_id),
      });

      setSuccess(true);
      toast.success("¡Gracias por compartir tu contenido!");
    } catch (error) {
      console.error("Error submitting:", error);
      toast.error("Error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (url.startsWith('/uploads/')) {
      return `${backendUrl}/api${url}`;
    }
    return `${backendUrl}${url}`;
  };

  if (success) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 pt-24 pb-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md text-center p-8"
          >
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-brand-text mb-4">
              ¡Gracias por compartir!
            </h1>
            <p className="text-brand-textSecondary mb-6">
              Hemos recibido tu contenido. Nuestro equipo lo revisará y lo publicará pronto.
            </p>
            <Button onClick={() => window.location.href = "/"}>
              Volver al inicio
            </Button>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        section="compartir"
        title="Compártenos tu Contenido"
        subtitle="Fotos o videos sobre tu comunidad, ciudad o región"
        badge="Contenido Comunitario"
        badgeIcon={Camera}
      />

      <section className="py-12 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-lg p-6 md:p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Información Personal */}
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-brand-text flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Información de Contacto
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nombre">Nombre completo *</Label>
                    <Input
                      id="nombre"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Tu nombre"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefono">Teléfono *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="telefono"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="+504 0000-0000"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="correo">Correo electrónico *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="correo"
                      type="email"
                      value={formData.correo}
                      onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                      placeholder="tu@email.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Ubicación */}
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-brand-text flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Ubicación
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="departamento">Departamento</Label>
                    <Select 
                      value={formData.departamento} 
                      onValueChange={(value) => setFormData({ ...formData, departamento: value, municipio: "" })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona departamento" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(hondurasDepartamentos).map((dep) => (
                          <SelectItem key={dep} value={dep}>{dep}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="municipio">Municipio</Label>
                    <Select 
                      value={formData.municipio} 
                      onValueChange={(value) => setFormData({ ...formData, municipio: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona municipio" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipios.map((mun) => (
                          <SelectItem key={mun} value={mun}>{mun}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Descripción */}
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-brand-text flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Descripción
                </h3>
                
                <div>
                  <Label htmlFor="descripcion">Cuéntanos sobre tu contenido *</Label>
                  <Textarea
                    id="descripcion"
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    placeholder="Describe las fotos o videos que compartes, dónde fueron tomados, qué eventos o lugares muestran..."
                    rows={4}
                    required
                  />
                </div>
              </div>

              {/* Fotos */}
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-brand-text flex items-center gap-2">
                  <Camera className="h-5 w-5 text-primary" />
                  Fotos (máximo 10)
                </h3>
                
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-brand-textSecondary">
                          Haz clic para subir fotos
                        </p>
                      </>
                    )}
                  </label>
                </div>

                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img
                          src={getImageUrl(photo.url)}
                          alt={`Foto ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Videos */}
              <div className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-brand-text flex items-center gap-2">
                  <Video className="h-5 w-5 text-primary" />
                  Videos (máximo 5, hasta 100MB cada uno)
                </h3>
                
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-primary/50 transition-colors">
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                  />
                  <label htmlFor="video-upload" className="cursor-pointer">
                    {uploading ? (
                      <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />
                    ) : (
                      <>
                        <Video className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-brand-textSecondary">
                          Haz clic para subir videos
                        </p>
                      </>
                    )}
                  </label>
                </div>

                {videos.length > 0 && (
                  <div className="space-y-2">
                    {videos.map((video, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Video className="h-5 w-5 text-gray-400" />
                          <span className="text-sm truncate">{video.filename}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeVideo(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl"
                disabled={loading || uploading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-5 w-5" />
                    Enviar Contenido
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                Al enviar, aceptas que revisaremos y podremos publicar tu contenido en choluteca.net
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
